import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./list_data_sales_invoice.css";
import useSWR from 'swr';
import axios from 'axios';

interface DataType {
    key: string;
    invoice_no: string;
    invoice_date: string;
    sales_order_no: string;
    packing_list_no: string;
    customer_name: string;
    sales_man: string;
    brand: string;
    profit_center: string;
    dealer_re_code: string;
    transaction_type: string;
    event_no: string;
    name_bill: string;
    status?: string;
}

type DataIndex = keyof DataType;

const fetcher = (url: string) => axios.get(url).then(res => res.data);

interface ListDataSalesInvoiceProps {
    onRowDoubleClick: (record: DataType) => void;
}


const ListDataSalesInvoice: React.FC<ListDataSalesInvoiceProps> = ({
    onRowDoubleClick,
}) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const { data, error } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/sales-invoices`, fetcher);
    const filteredData = data ? data.filter(item => item.status !== '4') : [];

    if (error) {
        console.error("Error fetching sales invoice data:", error);
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ?.toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()) ?? false,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            ...getColumnSearchProps('invoice_no'),
            sorter: (a, b) => a.invoice_no.localeCompare(b.invoice_no),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            ...getColumnSearchProps('invoice_date'),
            sorter: (a, b) => a.invoice_date.localeCompare(b.invoice_date),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Sales Order No',
            dataIndex: 'sales_order_no',
            key: 'sales_order_no',
            ...getColumnSearchProps('sales_order_no'),
            sorter: (a, b) => a.sales_order_no.localeCompare(b.sales_order_no),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Packing List No',
            dataIndex: 'packing_list_no',
            key: 'packing_list_no',
            ...getColumnSearchProps('packing_list_no'),
            sorter: (a, b) => a.packing_list_no.localeCompare(b.packing_list_no),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            ...getColumnSearchProps('customer_name'),
            sorter: (a, b) => a.customer_name.localeCompare(b.customer_name),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    return (

        <div id="ieml" style={transformStyle}>
            <div className="gjs-grid-row" id="ihqn">
                <div className="gjs-grid-column" id="is3l">
                    <Table<DataType>
                        columns={columns}
                        dataSource={filteredData}
                        loading={!data && !error}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                onRowDoubleClick(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ListDataSalesInvoice;